// src/pages/public/Home.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

/** --- Lightweight atoms --- **/
const Screen = ({ children }) => (
  <div style={{ backgroundColor: "#f7f5f2", minHeight: "100vh" }}>{children}</div>
);

const H1 = ({ children }) => (
  <div style={{ color: "#5c5470", fontSize: 32, fontWeight: 700, lineHeight: "55px" }}>
    {children}
  </div>
);

const Sub = ({ children, size = 14, bold = false, style }) => (
  <div
    style={{
      color: "#5c5470",
      fontSize: size,
      fontWeight: bold ? 700 : 400,
      lineHeight: size >= 16 ? "31px" : size >= 14 ? "25px" : "21px",
      ...style,
    }}
  >
    {children}
  </div>
);

const HeroImage = ({ src, height = 508 }) => (
  <div
    style={{
      width: "100%",
      height,
      backgroundImage: `url(${src})`,
      backgroundPosition: "center",
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      borderRadius: 16,
    }}
  />
);

const Icon = ({ src, w = 18, h = 18, alt = "", style }) => (
  <img src={src} alt={alt} style={{ width: w, height: h, display: "block", ...style }} />
);

const Pill = ({ children, color }) => (
  <div
    style={{
      width: "100%",
      backgroundColor: color,
      borderRadius: 50,
      padding: "12px 16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {children}
  </div>
);

const SoftCard = ({ children, style }) => (
  <div
    style={{
      backgroundColor: "#f0efeb",
      borderRadius: 10,
      padding: 12,
      ...style,
    }}
  >
    {children}
  </div>
);

/** --- Hours helpers --- **/
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getDayHours(dayIndex) {
    if (dayIndex === 0) {
      // Sunday
      return { openH: 11, openM: 0, closeH: 18, closeM: 0 };
    }
    // Monday–Saturday
    return { openH: 10, openM: 0, closeH: 20, closeM: 0 };
  }
  

function formatTime(h, m) {
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

function formatRange({ openH, openM, closeH, closeM }) {
  return `${formatTime(openH, openM)} – ${formatTime(closeH, closeM)}`;
}

function isOpenNow(now = new Date()) {
  const day = now.getDay();
  const { openH, openM, closeH, closeM } = getDayHours(day);
  const open = new Date(now);
  open.setHours(openH, openM, 0, 0);
  const close = new Date(now);
  close.setHours(closeH, closeM, 0, 0);
  return now >= open && now < close;
}

/** --- Hours block (shared) --- **/
function HoursBlock({
  open,
  todayRange,
  todayIdx,
  expanded,
  onToggle,
  ICON_CLOCK,
  ICON_DOT,
}) {
  const redFilter =
    "invert(12%) sepia(93%) saturate(7416%) hue-rotate(1deg) brightness(90%) contrast(122%)";
  const clockStyle = open ? {} : { filter: redFilter };

  return (
    <div style={{ marginTop: 12 }}>
      <H1>Hours</H1>

      {/* Summary row */}
      <SoftCard style={{ marginTop: 12 }}>
        <button
          onClick={onToggle}
          style={{
            width: "100%",
            background: "transparent",
            border: 0,
            padding: 0,
            display: "flex",
            alignItems: "center",
            gap: 12,
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          <Icon src={ICON_CLOCK} w={18} h={18} alt="clock" style={clockStyle} />
          <Sub size={16} bold style={{ color: open ? "#1f2937" : "#b91c1c" }}>
            {open ? "Open now" : "Closed"}
          </Sub>

          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Sub size={14} bold>
              {todayRange}
            </Sub>
            <Icon src={ICON_DOT} w={16} h={14} alt="dot" />
            <Sub size={14} style={{ opacity: 0.8 }}>
              {expanded ? "Hide" : "See week"}
            </Sub>
          </div>
        </button>
      </SoftCard>

      {/* Expanded week view */}
      {expanded && (
        <SoftCard style={{ marginTop: 10 }}>
          <div style={{ display: "grid", rowGap: 8 }}>
            {DAY_LABELS.map((label, idx) => {
              const range = formatRange(getDayHours(idx));
              const isToday = idx === todayIdx;
              return (
                <div
                  key={label}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "80px 1fr",
                    alignItems: "center",
                    gap: 12,
                    padding: "4px 2px",
                    background: isToday ? "#efecef" : "transparent",
                    borderRadius: isToday ? 6 : 0,
                  }}
                >
                  <Sub size={14} bold={isToday} style={{ opacity: isToday ? 1 : 0.85 }}>
                    {label}
                  </Sub>
                  <Sub size={14} bold={isToday}>
                    {range}
                  </Sub>
                </div>
              );
            })}
          </div>
        </SoftCard>
      )}
    </div>
  );
}

/** --- Hook: detect phone widths --- **/
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const handleResize = () => {
      if (typeof window === "undefined") return;
      setIsMobile(window.innerWidth <= breakpoint);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
}

/** --- Page --- **/
export default function Home() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const isMobile = useIsMobile();

  const HERO =
    "https://assets.api.uizard.io/api/cdn/stream/597fae2c-5bf0-46f2-ac43-e83205eb5d64.png";
  const ICON_STAR =
    "https://assets.api.uizard.io/api/cdn/stream/0f26a186-dbd8-42ea-8330-dc4f06a85a35.png";
  const ICON_PHONE =
    "https://assets.api.uizard.io/api/cdn/stream/fd3ec34b-bd8b-4155-b2ab-aeebecbbe48d.png";
  const ICON_PIN =
    "https://assets.api.uizard.io/api/cdn/stream/8ff9f8d5-fe3c-4f95-b730-0da986739f8a.png";
  const ICON_CLOCK =
    "https://assets.api.uizard.io/api/cdn/stream/c89abc90-53b1-47bf-99e0-52ea49069aa0.png";
  const ICON_DOT =
    "https://assets.api.uizard.io/api/cdn/stream/32e1e87d-d6db-4ae1-9cac-d0a5cbff3f9e.png";

  const now = new Date();
  const todayIdx = now.getDay();
  const todayHours = getDayHours(todayIdx);
  const open = useMemo(() => isOpenNow(now), [now]);
  const todayRange = useMemo(() => formatRange(todayHours), [todayIdx]);

  /** --- Desktop & tablet layout (no header here) --- **/
  const DesktopLayout = () => (
    <div
      style={{
        maxWidth: 1200,
        margin: "0 auto",
        padding: "24px 20px 40px",
        display: "grid",
        gridTemplateColumns: "1.2fr 0.8fr",
        gap: 24,
      }}
    >
      {/* Left: Image */}
      <HeroImage src={HERO} />

      {/* Right: Info */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 50 }}>
        {/* Phone */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon src={ICON_STAR} w={20} h={20} alt="phone" />
          <Sub size={13}>(613) 327-4049</Sub>
        </div>

        {/* Address */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon src={ICON_PIN} w={14} h={16} alt="address" />
          <Sub size={14} bold>
            598 Rideau St, Ottawa, ON K1N 6C6
          </Sub>
        </div>

        {/* CTA */}
        <div onClick={() => navigate("/booking")} style={{ cursor: "pointer" }}>
          <Pill color={"#c9ada7"}>
            <div style={{ color: "#fff", fontSize: 15, fontWeight: 700, lineHeight: "23px" }}>
              Book Now
            </div>
          </Pill>
        </div>

        {/* Hours */}
        <HoursBlock
          open={open}
          todayRange={todayRange}
          todayIdx={todayIdx}
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
          ICON_CLOCK={ICON_CLOCK}
          ICON_DOT={ICON_DOT}
        />
      </div>
    </div>
  );

  /** --- Phone layout (content only, header is in AppRouter) --- **/
  const MobileLayout = () => (
    <div
      style={{
        maxWidth: "100vw",
        margin: "0 auto",
        padding: "16px 16px 32px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      {/* Hero */}
      <HeroImage src={HERO} height={240} />

      {/* Phone */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Icon src={ICON_STAR} w={15} h={15} alt="phone" />
        <Sub size={13}>(613) 327-4049</Sub>
      </div>

      {/* Address */}
      <SoftCard>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon src={ICON_PIN} w={14} h={16} alt="address" />
          <Sub size={14} bold>
            598 Rideau St, Ottawa, ON K1N 6C6
          </Sub>
        </div>
      </SoftCard>

      {/* CTA */}
      <div onClick={() => navigate("/booking")} style={{ cursor: "pointer", width: "83vw"  }}>
        <Pill color={"#c9ada7"}>
          <div style={{ color: "#fff", fontSize: 15, fontWeight: 700, lineHeight: "23px"}}>
            Book Now
          </div>
        </Pill>
      </div>

      {/* Hours */}
      <HoursBlock
        open={open}
        todayRange={todayRange}
        todayIdx={todayIdx}
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        ICON_CLOCK={ICON_CLOCK}
        ICON_DOT={ICON_DOT}
      />
    </div>
  );

  return <Screen>{isMobile ? <MobileLayout /> : <DesktopLayout />}</Screen>;
}
